import { useCallback, useEffect, useRef, useState } from 'react';
import { decodeMessage, encodeAudioEvent, toPcm16 } from '../lib/awsEventStream';
import { getStreamUrl, parseSpokenValue, type SpokenValue } from '../services/voiceStreamService';

export type SpokenValueStatus = 'idle' | 'connecting' | 'listening' | 'processing' | 'error';
export type SpokenValueError = 'denied' | 'unsupported' | 'network' | 'consent' | 'credits' | 'disabled';

export interface SpokenValueResult extends SpokenValue {
  text: string;
}

/** Listening stops by itself after this long (push-to-talk, no always-on listening). */
export const MAX_LISTEN_MS = 15_000;
/** After "stop", wait this long for Transcribe's final result. */
const FINAL_WAIT_MS = 2_500;

interface TranscribeResult {
  IsPartial?: boolean;
  Alternatives?: Array<{ Transcript?: string }>;
}

function apiErrorCode(err: unknown): string | undefined {
  return (err as { code?: string } | null)?.code;
}

/**
 * "Say the value" in the guided test session (docs/voiceFeature.md, UC-15):
 * while listening, the microphone is streamed as 16 kHz PCM straight to Amazon
 * Transcribe Streaming over a presigned WebSocket; nothing passes through
 * ReefGuide and nothing is stored. On stop, the final text goes to the
 * deterministic parser (numbers + "next" / "repeat" / "skip") — no AI.
 */
export function useSpokenValue(aquariumId: string, sessionId: string, language: string, onResult: (result: SpokenValueResult) => void) {
  const [status, setStatus] = useState<SpokenValueStatus>('idle');
  const [partial, setPartial] = useState('');
  const [error, setError] = useState<SpokenValueError | null>(null);
  const socket = useRef<WebSocket | null>(null);
  const stream = useRef<MediaStream | null>(null);
  const audio = useRef<AudioContext | null>(null);
  const finals = useRef<string[]>([]);
  const stopTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onResultRef = useRef(onResult);
  onResultRef.current = onResult;

  const release = useCallback(() => {
    if (stopTimer.current) clearTimeout(stopTimer.current);
    stopTimer.current = null;
    stream.current?.getTracks().forEach((track) => track.stop());
    stream.current = null;
    void audio.current?.close().catch(() => undefined);
    audio.current = null;
  }, []);

  const finish = useCallback(async () => {
    const ws = socket.current;
    socket.current = null;
    if (ws && ws.readyState === WebSocket.OPEN) ws.close();
    const text = finals.current.join(' ').trim();
    finals.current = [];
    setPartial('');
    if (text === '') {
      setStatus('idle');
      return;
    }
    try {
      const parsed = await parseSpokenValue(aquariumId, text, language);
      setStatus('idle');
      onResultRef.current({ ...parsed, text });
    } catch {
      setError('network');
      setStatus('error');
    }
  }, [aquariumId, language]);

  const stop = useCallback(() => {
    if (status !== 'listening' && status !== 'connecting') return;
    release();
    const ws = socket.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      setStatus('processing');
      // An empty audio event ends the stream; Transcribe then sends the last final result.
      ws.send(encodeAudioEvent(new Uint8Array(0)));
      stopTimer.current = setTimeout(() => void finish(), FINAL_WAIT_MS);
    } else {
      void finish();
    }
  }, [finish, release, status]);

  const start = useCallback(async () => {
    if (status === 'listening' || status === 'connecting' || status === 'processing') return;
    if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia || typeof WebSocket === 'undefined') {
      setError('unsupported');
      setStatus('error');
      return;
    }
    setError(null);
    setPartial('');
    finals.current = [];
    setStatus('connecting');
    let url;
    try {
      url = await getStreamUrl(aquariumId, sessionId, language);
    } catch (err) {
      const code = apiErrorCode(err);
      setError(
        code === 'VOICE_CONSENT_REQUIRED' ? 'consent' : code === 'AI_BUDGET_EXCEEDED' ? 'credits' : code === 'VOICE_CAPTURE_DISABLED' ? 'disabled' : 'network',
      );
      setStatus('error');
      return;
    }
    try {
      stream.current = await navigator.mediaDevices.getUserMedia({ audio: { channelCount: 1, echoCancellation: true, noiseSuppression: true } });
    } catch {
      setError('denied');
      setStatus('error');
      return;
    }

    const ws = new WebSocket(url.url);
    ws.binaryType = 'arraybuffer';
    socket.current = ws;
    ws.onmessage = (event) => {
      try {
        const message = decodeMessage(new Uint8Array(event.data as ArrayBuffer));
        if (message.headers[':message-type'] !== 'event') {
          setError('network');
          return;
        }
        const body = JSON.parse(new TextDecoder().decode(message.payload)) as { Transcript?: { Results?: TranscribeResult[] } };
        for (const result of body.Transcript?.Results ?? []) {
          const text = result.Alternatives?.[0]?.Transcript ?? '';
          if (result.IsPartial) setPartial(text);
          else if (text) {
            finals.current.push(text);
            setPartial('');
          }
        }
      } catch {
        // A broken frame: ignore it, the next one carries the text again.
      }
    };
    ws.onerror = () => setError('network');
    ws.onclose = () => {
      if (stopTimer.current) {
        clearTimeout(stopTimer.current);
        stopTimer.current = null;
        void finish();
      }
    };
    ws.onopen = () => {
      const Ctx = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      const ctx = new Ctx();
      audio.current = ctx;
      const source = ctx.createMediaStreamSource(stream.current!);
      // ScriptProcessor: deprecated but available everywhere (no worklet file to ship).
      const processor = ctx.createScriptProcessor(4096, 1, 1);
      processor.onaudioprocess = (e) => {
        if (ws.readyState === WebSocket.OPEN) ws.send(encodeAudioEvent(toPcm16(e.inputBuffer.getChannelData(0), ctx.sampleRate, url.sampleRate)));
      };
      source.connect(processor);
      processor.connect(ctx.destination);
      setStatus('listening');
      stopTimer.current = setTimeout(() => stopRef.current(), MAX_LISTEN_MS);
    };
  }, [aquariumId, finish, language, sessionId, status]);

  const stopRef = useRef(stop);
  stopRef.current = stop;

  // Leaving the page ends listening.
  useEffect(
    () => () => {
      release();
      socket.current?.close();
    },
    [release],
  );

  const reset = useCallback(() => {
    setError(null);
    setStatus('idle');
  }, []);

  return { status, partial, error, start, stop, reset };
}
